"use client";

import React, { useState } from "react";
import { Modal } from "./Modal";
import { ActionButton } from "../buttons/ActionButton";
import { Timeframe } from "../../lib/enums/timeframe.enum";

interface AddTickerModalProps {
  isVisible: boolean;
  onClose: () => void;
  onAdd: (tickerData: {
    symbol: string;
    pair: string;
    exchange: string;
    candleSyncTimeframes: Timeframe[];
  }) => Promise<boolean>;
  exchanges: string[];
  timeframes: Timeframe[];
}

export const AddTickerModal: React.FC<AddTickerModalProps> = ({
  isVisible,
  onClose,
  onAdd,
  exchanges,
  timeframes,
}) => {
  const [symbol, setSymbol] = useState("");
  const [pair, setPair] = useState("");
  const [exchange, setExchange] = useState("");
  const [selectedTimeframes, setSelectedTimeframes] = useState<Timeframe[]>([]);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const success = await onAdd({
        symbol,
        pair,
        exchange,
        candleSyncTimeframes: selectedTimeframes,
      });

      if (success) {
        setResponseMessage("Ticker added successfully!");
        setIsError(false);
        resetForm();
      } else {
        setResponseMessage("Failed to add ticker. Please try again.");
        setIsError(true);
      }
    } catch {
      setResponseMessage("Failed to add ticker. Please try again.");
      setIsError(true);
    } finally {
      setTimeout(() => setResponseMessage(null), 5000);
    }
  };

  const resetForm = () => {
    setSymbol("");
    setPair("");
    setExchange("");
    setSelectedTimeframes([]);
  };

  const handleTimeframeChange = (tf: Timeframe) => {
    setSelectedTimeframes((prev) =>
      prev.includes(tf) ? prev.filter((t) => t !== tf) : [...prev, tf]
    );
  };

  return (
    <Modal isVisible={isVisible} onClose={onClose} title="Add New Ticker">
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="space-y-4"
      >
        <div>
          <label
            htmlFor="symbol"
            className="block text-sm font-medium text-gray-300"
          >
            Symbol
          </label>
          <input
            type="text"
            id="symbol"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="mt-1 block w-full h-10 rounded-md bg-slate-700 border-gray-600 text-white px-3"
            required
          />
        </div>

        <div>
          <label
            htmlFor="pair"
            className="block text-sm font-medium text-gray-300"
          >
            Pair
          </label>
          <input
            type="text"
            id="pair"
            value={pair}
            onChange={(e) => setPair(e.target.value)}
            className="mt-1 block w-full h-10 rounded-md bg-slate-700 border-gray-600 text-white px-3"
            required
          />
        </div>

        <div>
          <label
            htmlFor="exchange"
            className="block text-sm font-medium text-gray-300"
          >
            Exchange
          </label>
          <select
            id="exchange"
            value={exchange}
            onChange={(e) => setExchange(e.target.value)}
            className="mt-1 block w-full h-10 rounded-md bg-slate-700 border-gray-600 text-white px-3"
            required
          >
            <option value="">Select an exchange</option>
            {exchanges.map((ex) => (
              <option key={ex} value={ex}>
                {ex}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300">
            Timeframes
          </label>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {timeframes.map((tf) => (
              <label key={tf} className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={selectedTimeframes.includes(tf)}
                  onChange={() => handleTimeframeChange(tf)}
                  className="rounded bg-slate-700 border-gray-600 text-blue-600"
                />
                <span className="ml-2 text-white">{tf}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <ActionButton onClick={onClose} label="Cancel" variant="secondary" />
          <ActionButton
            onClick={() => {}}
            label="Add Ticker"
            variant="primary"
            className="submit-button"
          />
        </div>
      </form>

      {responseMessage && (
        <div
          className={`fixed mt-4 top-16 p-4 rounded ${
            isError ? "bg-red-700" : "bg-green-700"
          } text-white`}
        >
          {responseMessage}
        </div>
      )}
    </Modal>
  );
};
